import { useEffect } from 'react';
import { CapacitorShareTarget } from '@capgo/capacitor-share-target';
import type { ShareReceivedEvent } from '@capgo/capacitor-share-target';
import { useShareTargetContext } from '@/contexts/ShareTargetContext';
import { useAuth } from '@/hooks/useAuth';

const URL_REGEX = /https?:\/\/[^\s]+/i;

/**
 * Extracts a URL from a share event. URLs arrive in the texts array — either
 * as a bare URL or embedded in a longer string (e.g. an Instagram caption).
 */
export function extractUrlFromShareEvent(event: ShareReceivedEvent): string | null {
  for (const text of event.texts) {
    const trimmed = text.trim();
    if (!trimmed) continue;

    // Bare URL — must be http or https
    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        return trimmed;
      }
    } catch {
      // Not a bare URL — try extracting one from the text
    }

    // URL embedded in text (e.g. TikTok / Instagram captions)
    const match = trimmed.match(URL_REGEX);
    if (match) return match[0];
  }
  return null;
}

/**
 * Listens for share target events from the OS share sheet and stores the shared
 * URL in ShareTargetContext. If the user is not authenticated, triggers login
 * first — the WebView stays alive through the native login flow, so the pending
 * URL survives in context and ShareTargetPrompt picks it up after auth resolves.
 *
 * Must be called inside both Auth0Provider and ShareTargetProvider.
 */
export function useShareTargetListener() {
  const { setPendingShareUrl, pendingShareUrl } = useShareTargetContext();
  const { isAuthenticated, isLoading, login } = useAuth();

  // Register the native share event listener.
  useEffect(() => {
    let listenerHandle: Awaited<ReturnType<typeof CapacitorShareTarget.addListener>> | null = null;

    CapacitorShareTarget.addListener('shareReceived', (event) => {
      const url = extractUrlFromShareEvent(event);
      if (url) setPendingShareUrl(url);
    }).then((handle) => {
      listenerHandle = handle;
    });

    return () => {
      listenerHandle?.remove();
    };
  }, [setPendingShareUrl]);

  // Once auth state has resolved: if there is a pending URL and the user is
  // not logged in, trigger login. The WebView stays alive through the native
  // Browser.open login flow, so pendingShareUrl survives in context.
  useEffect(() => {
    if (isLoading || !pendingShareUrl) return;
    if (!isAuthenticated) {
      login();
    }
  }, [isLoading, isAuthenticated, pendingShareUrl, login]);
}
